"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, SlidersHorizontal as Sliders, GitBranch, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const workflowSteps = [{
  stepNumber: "01",
  title: "Detector Signal Monitoring",
  description: "Continuous optical and electronic signal tracking to capture instrument responses in real time.",
  metricLabel: "Status",
  metricValue: "Active Stream"
}, {
  stepNumber: "02",
  title: "Baseline Discrimination",
  description: "Algorithmic verification separating true measurement thresholds from ambient noise and drift.",
  metricLabel: "Filter",
  metricValue: "Calibrated"
}, {
  stepNumber: "03",
  title: "Automated Valve Diversion",
  description: "Direct mechanical actuation redirecting non-compliant samples to maintain system purity.",
  metricLabel: "Response",
  metricValue: "Automated"
}, {
  stepNumber: "04",
  title: "Continuous Loop Integrity",
  description: "Constant verification across the analytical pipeline to validate ongoing instrument performance.",
  metricLabel: "Verification",
  metricValue: "Continuously Monitored"
}];
const stepIcons = [Activity, Sliders, GitBranch, ShieldCheck];
export default function ProcessWorkflow() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="processworkflow" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border relative overflow-hidden">
      <div className="page-container relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider border-primary/40 text-primary bg-primary/5 rounded-full">
              Operational Architecture
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Precision Analytical Process Workflow
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A verified 4-step sequence engineered for regulated laboratories and
            manufacturing teams across Ireland and the UK.
          </p>
        </div>

        {/* 4-Step Process Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {workflowSteps.map((step, index) => {
          const IconComponent = stepIcons[index] || Activity;
          const isLast = index === workflowSteps.length - 1;
          return <motion.div key={step.stepNumber} data-index={index} variants={itemVariants} className="relative flex flex-col h-full">
                <Card className="h-full flex flex-col justify-between border-border hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-md bg-card relative overflow-hidden group">
                  {/* Top Orange Indicator Stripe */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-muted group-hover:bg-primary transition-colors duration-300" />

                  <div>
                    <CardHeader className="pb-4 pt-6">
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <span className="text-2xl font-bold font-mono text-primary">
                          {step.stepNumber}
                        </span>
                        <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <IconComponent className="size-5" />
                        </div>
                      </div>
                      <CardTitle className="text-lg font-default font-semibold text-card-foreground group-hover:text-primary transition-colors">
                        {step.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="pt-0 pb-4">
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {step.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  {/* Card Bottom Meta */}
                  <div className="px-6 pb-6 pt-4 mt-auto border-t border-border/60 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {step.metricLabel}
                    </span>
                    <span className="font-semibold text-foreground flex items-center gap-1.5">
                      <CheckCircle2 className="size-3.5 text-primary" />
                      {step.metricValue}
                    </span>
                  </div>
                </Card>

                {/* Orange Connector Arrow for Desktop (Between steps) */}
                {!isLast && <div className="hidden lg:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-20 size-7 rounded-full bg-background border border-primary/30 text-primary shadow-xs items-center justify-center">
                    <ChevronRight className="size-4 stroke-[2.5]" />
                  </div>}
              </motion.div>;
        })}
        </motion.div>

        {/* Process Support & CTA Banner */}
        <div className="mt-14 p-6 sm:p-8 rounded-xl bg-secondary border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-lg font-semibold text-secondary-foreground">
              Need detailed lifecycle documentation for your equipment?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our engineering team assists with system qualification, validation
              protocols, and maintenance schedules.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild variant="outline" size="default">
              <Link to="/SolutionsPage">View Solutions</Link>
            </Button>
            <Button asChild variant="default" size="default">
              <Link to="/ContactPage" className="gap-2">
                Contact Specialists
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}